import { useEffect, useRef } from 'react';

const ParticleBackground = ({ variant = 'particles' }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const pixelsRef = useRef([]);
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: -9999, y: -9999, active: false });
  const lastTimeRef = useRef(0);
  const ripplesRef = useRef([]);
  const shootingStarsRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }

    let width = 0;
    let height = 0;
    let dpr = window.devicePixelRatio || 1;
    const prefersReducedMotion = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const getGreen = (alpha) => `rgba(255, 255, 255, ${alpha})`;
    const getLight = (alpha) => `rgba(203, 213, 225, ${alpha})`;
    const getDotColor = (alpha) => `rgba(255, 255, 255, ${alpha})`;
    const getLineColor = (alpha) => `rgba(160, 160, 160, ${alpha})`;

    const resizeCanvas = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      dpr = window.devicePixelRatio || 1;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    if (prefersReducedMotion) {
      resizeCanvas();
      context.clearRect(0, 0, width, height);
      const handleResize = () => {
        resizeCanvas();
        context.clearRect(0, 0, width, height);
      };
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }

    if (variant === 'uniform') {
      resizeCanvas();
      context.clearRect(0, 0, width, height);
      const handleResize = () => {
        resizeCanvas();
        context.clearRect(0, 0, width, height);
      };
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }

    // ─── Multi-octave noise-like function ───
    const noiseField = (x, y, t) => {
      // 4 octaves of layered sine/cosine for organic turbulence
      const o1 = Math.sin(x * 0.003 + t) + Math.cos(y * 0.003 + t * 0.7);
      const o2 = Math.sin(x * 0.007 - t * 0.5) * Math.cos(y * 0.006 + t * 0.3) * 0.5;
      const o3 = Math.sin((x + y) * 0.002 + t * 0.4) * 0.35;
      const o4 = Math.cos((x - y) * 0.004 + t * 0.9) * 0.2;
      return (o1 + o2 + o3 + o4) * Math.PI;
    };

    const setupPixels = () => {
      // Force the cursor inactive for the pixel background so no hover area can
      // form (and so a stale active state from a prior variant can't carry over).
      mouseRef.current = { x: -9999, y: -9999, active: false };
      const isSoft = variant === 'pixel-soft';
      const pixelConfig = isSoft
        ? { speed: 0.35, wobble: 0.3, alpha: 0.55, jitter: 0.2, density: 0.35 }
        : { speed: 1, wobble: 1, alpha: 1, jitter: 1, density: 0.92 };

      const createPixels = () => {
        const spacing = Math.max(isSoft ? 10 : 6, Math.floor(Math.min(width, height) / 130));
        const pixels = [];
        for (let x = 0; x < width; x += spacing) {
          for (let y = 0; y < height; y += spacing) {
            const density = (0.52 + Math.random() * 0.4) * pixelConfig.density;
            if (Math.random() > density) continue;

            // ── Enhancement 1: Depth Parallax ──
            const depth = 0.3 + Math.random() * 0.7; // 0.3 = far, 1.0 = near
            const baseSize = (1.0 + Math.random() * 1.4) * depth;
            const velocity = (0.12 + Math.random() * 0.18) * pixelConfig.speed * depth;
            const hueType = Math.random() > 0.55 ? 'light' : 'green';

            pixels.push({
              originX: x,
              originY: y,
              depth,
              size: baseSize,
              velocity,
              baseAlpha: (0.25 + Math.random() * 0.45) * depth,
              phase: Math.random() * Math.PI * 2,
              hueType,
              repelX: 0,
              repelY: 0
            });
          }
        }
        pixelsRef.current = pixels;
        ripplesRef.current = [];
        shootingStarsRef.current = [];
      };

      const resize = () => {
        resizeCanvas();
        createPixels();
      };

      // The pixel background is intentionally non-interactive: it must not react
      // to the cursor (no swirl/repel/ripple cluster forming on hover). The
      // mouse-driven branches below stay guarded by `mouse.active`, which is
      // never set true, so pixels just drift ambiently.

      const animate = (time) => {
        const now = time || 0;
        const rawDelta = now - lastTimeRef.current;
        const deltaTime = Math.min(rawDelta, 60) / 16.66;
        lastTimeRef.current = now;

        const t = now * 0.0006;

        // ── Enhancement 4: Trail Afterglow ──
        // Instead of clearRect, overlay a semi-transparent black to create fading trails
        context.fillStyle = 'rgba(0, 0, 0, 0.12)';
        context.fillRect(0, 0, width, height);

        // ── Enhancement 5: Pulse Breathing ──
        const breathe = 0.85 + 0.15 * Math.sin(now * 0.0008);

        const pixels = pixelsRef.current;
        const mouse = mouseRef.current;
        const ripples = ripplesRef.current;

        // Update ripples
        for (let r = ripples.length - 1; r >= 0; r--) {
          const rip = ripples[r];
          rip.radius += rip.speed * deltaTime;
          rip.alpha *= Math.pow(0.97, deltaTime);
          if (rip.radius > rip.maxRadius || rip.alpha < 0.01) {
            ripples.splice(r, 1);
          }
        }

        // ── Enhancement 7: Occasional Shooting Stars ──
        if (!isSoft && Math.random() < 0.008 * deltaTime) {
          const angle = -Math.PI / 6 + (Math.random() - 0.5) * 0.4;
          shootingStarsRef.current.push({
            x: Math.random() * width * 0.5 + width * 0.25,
            y: -10,
            vx: Math.cos(angle) * (8 + Math.random() * 6),
            vy: Math.sin(angle + Math.PI / 2) * (8 + Math.random() * 6),
            life: 1.0,
            length: 40 + Math.random() * 60,
            size: 1.5 + Math.random() * 1.5
          });
          if (shootingStarsRef.current.length > 6) shootingStarsRef.current.shift();
        }

        // Process & render particles
        for (let i = 0; i < pixels.length; i += 1) {
          const p = pixels[i];

          // ── Enhancement 2: Organic Perlin-like Noise Flow ──
          const angle = noiseField(p.originX, p.originY, t);
          const flowForce = 0.14 * p.depth; // deeper = slower
          p.originX += Math.cos(angle) * flowForce * p.velocity * deltaTime;
          p.originY += Math.sin(angle) * flowForce * p.velocity * deltaTime;

          // Brownian micro-jitter
          const jit = 0.06 * pixelConfig.jitter;
          p.originX += (Math.random() - 0.5) * jit * deltaTime;
          p.originY += (Math.random() - 0.5) * jit * deltaTime;

          // Screen wrap
          const pad = 20;
          if (p.originX < -pad) p.originX = width + pad + Math.random() * 10;
          else if (p.originX > width + pad) p.originX = -pad - Math.random() * 10;
          if (p.originY < -pad) p.originY = height + pad + Math.random() * 10;
          else if (p.originY > height + pad) p.originY = -pad - Math.random() * 10;

          // Mouse Magnetic Swirl & Repel
          if (mouse.active) {
            const dx = p.originX - mouse.x;
            const dy = p.originY - mouse.y;
            const dist = Math.hypot(dx, dy);
            const mouseRadius = 80 * p.depth; // far particles less affected

            if (dist < mouseRadius && dist > 0.01) {
              const force = (mouseRadius - dist) / mouseRadius;
              const swirlAngle = Math.atan2(dy, dx) + Math.PI / 2;
              p.repelX += Math.cos(swirlAngle) * force * 0.9 * deltaTime;
              p.repelY += Math.sin(swirlAngle) * force * 0.9 * deltaTime;
              p.repelX += (dx / dist) * force * 0.5 * deltaTime;
              p.repelY += (dy / dist) * force * 0.5 * deltaTime;
            }
          }

          // ── Enhancement 3: Ripple displacement ──
          for (let r = 0; r < ripples.length; r++) {
            const rip = ripples[r];
            const rdx = p.originX - rip.x;
            const rdy = p.originY - rip.y;
            const rdist = Math.hypot(rdx, rdy);
            const ringDist = Math.abs(rdist - rip.radius);

            if (ringDist < 20 && rdist > 0.01) {
              const rippleForce = (1 - ringDist / 20) * rip.alpha * 1.0 * p.depth;
              p.repelX += (rdx / rdist) * rippleForce * deltaTime;
              p.repelY += (rdy / rdist) * rippleForce * deltaTime;
            }
          }

          // Inertia damping
          const damp = Math.pow(0.90, deltaTime);
          p.repelX *= damp;
          p.repelY *= damp;

          // ── Visuals ──
          const speed = Math.hypot(p.repelX, p.repelY);
          const shimmer = 0.5 + 0.5 * Math.sin(now * 0.0012 + p.phase);
          let alpha = Math.max(0.03, p.baseAlpha * shimmer * (0.3 + speed * 0.12) * pixelConfig.alpha * breathe);

          const renderX = p.originX + p.repelX;
          const renderY = p.originY + p.repelY;

          // ── Enhancement 6: Edge Vignette Fade ──
          const edgeMargin = 120;
          const edgeFadeX = Math.min(renderX / edgeMargin, (width - renderX) / edgeMargin, 1);
          const edgeFadeY = Math.min(renderY / edgeMargin, (height - renderY) / edgeMargin, 1);
          const edgeFade = Math.max(0, Math.min(edgeFadeX, edgeFadeY));
          alpha *= edgeFade;

          if (alpha < 0.01) continue;

          // Chromatic variance: speed-based color shifts
          const renderSize = p.size * (1 + speed * 0.08) * breathe;
          if (speed > 1.5) {
            context.fillStyle = speed > 3 ? getLineColor(alpha * 0.9) : getGreen(alpha);
          } else {
            context.fillStyle = p.hueType === 'green' ? getGreen(alpha) : getLight(alpha * 0.55);
          }

          context.fillRect(renderX, renderY, renderSize, renderSize);
        }

        // ── Render Shooting Stars ──
        const stars = shootingStarsRef.current;
        for (let s = stars.length - 1; s >= 0; s--) {
          const star = stars[s];
          star.x += star.vx * deltaTime;
          star.y += star.vy * deltaTime;
          star.life -= 0.012 * deltaTime;

          if (star.life <= 0 || star.x > width + 50 || star.y > height + 50) {
            stars.splice(s, 1);
            continue;
          }

          const tailX = star.x - (star.vx / Math.hypot(star.vx, star.vy)) * star.length * star.life;
          const tailY = star.y - (star.vy / Math.hypot(star.vx, star.vy)) * star.length * star.life;

          const grad = context.createLinearGradient(tailX, tailY, star.x, star.y);
          grad.addColorStop(0, `rgba(255, 255, 255, 0)`);
          grad.addColorStop(0.6, `rgba(160, 160, 160, ${0.18 * star.life})`);
          grad.addColorStop(1, `rgba(255, 255, 255, ${0.8 * star.life})`);

          context.strokeStyle = grad;
          context.lineWidth = star.size * star.life;
          context.lineCap = 'round';
          context.beginPath();
          context.moveTo(tailX, tailY);
          context.lineTo(star.x, star.y);
          context.stroke();

          // Bright head glow
          context.fillStyle = `rgba(255, 255, 255, ${0.9 * star.life})`;
          context.beginPath();
          context.arc(star.x, star.y, star.size * 0.8 * star.life, 0, Math.PI * 2);
          context.fill();
        }

        // ── Render Ripple Rings (subtle visual feedback) ──
        for (let r = 0; r < ripples.length; r++) {
          const rip = ripples[r];
          context.strokeStyle = `rgba(255, 255, 255, ${rip.alpha * 0.05})`;
          context.lineWidth = 0.5;
          context.beginPath();
          context.arc(rip.x, rip.y, rip.radius, 0, Math.PI * 2);
          context.stroke();
        }

        animationRef.current = requestAnimationFrame(animate);
      };

      resize();
      window.addEventListener('resize', resize);
      animationRef.current = requestAnimationFrame(animate);

      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
        window.removeEventListener('resize', resize);
      };
    };

    const setupParticles = () => {
      const createParticles = () => {
        const count = Math.min(220, Math.max(90, Math.floor((width * height) / 10000)));
        particlesRef.current = Array.from({ length: count }, () => {
          const px = Math.random() * width;
          const py = Math.random() * height;
          return {
            x: px,
            y: py,
            prevX: px,
            prevY: py,
            vx: (Math.random() - 0.5) * 0.8,
            vy: (Math.random() - 0.5) * 0.8,
            size: Math.random() * 2.2 + 1.2,
            phase: Math.random() * Math.PI * 2,
            wobble: Math.random() * 0.02,
            mass: 0.8 + Math.random() * 0.6,
            brightness: 0.3 + Math.random() * 0.7,
            justWrapped: false
          };
        });
      };

      const resize = () => {
        resizeCanvas();
        createParticles();
      };

      const handleMouseMove = (event) => {
        mouseRef.current = { x: event.clientX, y: event.clientY, active: true };
      };

      const handleMouseLeave = () => {
        mouseRef.current.active = false;
      };

      const animate = (time) => {
        const now = time || 0;
        const deltaTime = Math.min(now - lastTimeRef.current, 60) / 16.66;
        lastTimeRef.current = now;

        context.clearRect(0, 0, width, height);
        const particles = particlesRef.current;
        const mouse = mouseRef.current;
        const flowTime = now * 0.0005;

        for (let i = 0; i < particles.length; i += 1) {
          const p = particles[i];
          p.prevX = p.x;
          p.prevY = p.y;
          p.justWrapped = false;

          // Cyber-Fluid Flow Field Logic (Multi-layered Turbulence)
          // Mixing x and y coordinates prevents stable stationary lines (manifolds) from forming
          const angle = (
            Math.sin(p.x * 0.003 + flowTime) +
            Math.cos(p.y * 0.003 + flowTime * 0.8) +
            Math.sin((p.x - p.y) * 0.002 + flowTime * 0.5)
          ) * Math.PI;
          const fx = Math.cos(angle) * 0.15;
          const fy = Math.sin(angle) * 0.15;

          p.vx += fx * deltaTime;
          p.vy += fy * deltaTime;

          // Brownian Escape: Give a random kick to slow/stuck particles
          const currentSpeed = Math.hypot(p.vx, p.vy);
          const escapeThreshold = 0.25;
          if (currentSpeed < escapeThreshold) {
            p.vx += (Math.random() - 0.5) * 0.2 * deltaTime;
            p.vy += (Math.random() - 0.5) * 0.2 * deltaTime;
          }

          // Small constant velocity jitter
          p.vx += (Math.random() - 0.5) * 0.02 * deltaTime;
          p.vy += (Math.random() - 0.5) * 0.02 * deltaTime;

          if (mouse.active) {
            const dx = p.x - mouse.x;
            const dy = p.y - mouse.y;
            const dist = Math.hypot(dx, dy);

            if (dist < 200) {
              const force = (1 - dist / 200) * 1.5;

              // Magnetic Swirl Effect
              const swirlAngle = Math.atan2(dy, dx) + Math.PI / 2;
              p.vx += Math.cos(swirlAngle) * force * 0.8 * deltaTime;
              p.vy += Math.sin(swirlAngle) * force * 0.8 * deltaTime;

              // Gentle Repel
              p.vx += (dx / dist) * force * 0.4 * deltaTime;
              p.vy += (dy / dist) * force * 0.4 * deltaTime;
            }
          }

          // Friction & Velocity (dt scale)
          const friction = Math.pow(0.96, deltaTime);
          p.vx *= friction;
          p.vy *= friction;
          p.x += (p.vx / p.mass) * deltaTime;
          p.y += (p.vy / p.mass) * deltaTime;

          // Wrap Around with Padding and random offset to break resonance
          const pad = 40;
          if (p.x < -pad) { p.x = width + pad + Math.random() * 15; p.justWrapped = true; }
          else if (p.x > width + pad) { p.x = -pad - Math.random() * 15; p.justWrapped = true; }
          if (p.y < -pad) { p.y = height + pad + Math.random() * 15; p.justWrapped = true; }
          else if (p.y > height + pad) { p.y = -pad - Math.random() * 15; p.justWrapped = true; }

          // Rendering with Chromatic Variance
          const movementSpeed = Math.hypot(p.vx, p.vy);
          const twinkle = 0.4 + 0.6 * Math.sin(now * 0.002 + p.phase);

          context.fillStyle = movementSpeed > 1.5
            ? getLineColor(twinkle * 0.5 * p.brightness)
            : getDotColor(twinkle * 0.4 * p.brightness);

          context.beginPath();
          context.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          context.fill();
        }

        // Connection Lines with Velocity-based Alpha
        for (let i = 0; i < particles.length; i += 1) {
          const a = particles[i];
          if (a.justWrapped) continue; // Skip lines for just wrapped particles

          for (let j = i + 1; j < particles.length; j += 1) {
            const b = particles[j];
            if (b.justWrapped) continue;

            const dx = a.x - b.x;
            const dy = a.y - b.y;
            // optimization: check dx/dy individually before hypers
            if (Math.abs(dx) > 140 || Math.abs(dy) > 140) continue;

            const distSq = dx * dx + dy * dy;
            const maxDistSq = 140 * 140;

            if (distSq < maxDistSq) {
              const dist = Math.sqrt(distSq);
              const speedFactor = (Math.hypot(a.vx, a.vy) + Math.hypot(b.vx, b.vy)) * 0.5;
              const alpha = (1 - dist / 140) * (0.12 + speedFactor * 0.1);

              context.strokeStyle = speedFactor > 1.2 ? getLineColor(alpha) : getDotColor(alpha);
              context.lineWidth = 0.8;
              context.beginPath();
              context.moveTo(a.x, a.y);
              context.lineTo(b.x, b.y);
              context.stroke();
            }
          }
        }

        animationRef.current = requestAnimationFrame(animate);
      };

      resize();
      window.addEventListener('resize', resize);
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseleave', handleMouseLeave);
      animationRef.current = requestAnimationFrame(animate);

      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
        window.removeEventListener('resize', resize);
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseleave', handleMouseLeave);
      };
    };

    const cleanup = variant === 'particles' ? setupParticles() : setupPixels();
    return cleanup;
  }, [variant]);

  return <canvas ref={canvasRef} className={`particle-canvas variant-${variant}`} aria-hidden="true" />;
};

export default ParticleBackground;
